import { IntakeLabel, IntakeLabelCollection } from '../../entities/intake-label.interface';
import { MinistryList } from '../../constants/ministry-list';
import { ProgramList } from '../../constants/program-list';
import { CRA_NAME_LONG } from '../../constants/global-names-lookup';

const diaName = ProgramList.dia;
const aishName = ProgramList.aish;
const adapName = ProgramList.adap;

export const AishConsentLabels: IntakeLabelCollection = {
  agreePrivacyContent: {
    label: `The personal information you provide is being collected to determine your eligibility for different social-based supports and benefits offered by the Government of Alberta under Alberta Supports.
    <br /><br /> 
    If you choose to apply, the personal information you provide will be entered into an automated system to validate self-reported information.
    <br /><br />
    The personal information provided to Alberta Supports is collected under the authority of sections 4(a) and 4(c) of the <em>Protection of Privacy Act</em> and section 5 of the <em>${aishName.fullName} Act</em>. 
    <br/><br/>
    If you have questions about the collection of personal information, please contact the Alberta Supports Contact Centre toll-free at <a href="tel:1-877-644-9992" class="bold">1-877-644-9992</a>, from 7:30 a.m. to 8:00 p.m. every Monday to Friday, except statutory holidays.`,
  },
  agreeAishConsent: {
    label: `I have read and agree to the ${aishName.abbreviation} and ${adapName.abbreviation} consent above.`,
  },
  agreeAishConsentContent: {
    label: `I give my permission to any person, agency, organization, institution or other source to give ${aishName.abbreviation} and ${adapName.abbreviation} 
    and/or ${aishName.abbreviation} and ${adapName.abbreviation} contracted services any information about my household situation, education and training, 
    employment, and finances ${aishName.abbreviation} or ${adapName.abbreviation} requests to determine my eligibility 
    for ${aishName.abbreviation} or ${adapName.abbreviation} benefits.
    <br /><br />
    I understand I may withdraw my consent, in writing, at any time.
    <br /><br />Please note that while consent can be withdrawn, in writing, at any time, doing so may impact your eligibility 
    for ${aishName.abbreviation} and ${adapName.abbreviation} benefits.`,
  },
  agreeCraConsent: {
    label: 'I have read and agree to the Canada Revenue Agency Consent above.',
  },
  agreeCraConsentContent: {
    // label: `I consent to the release, by the Canada Revenue Agency to the Alberta Ministry of ${MinistryList['scss']?.displayValue},
    // of information from my income tax returns and other required taxpayer information about me whether supplied by me or a third party.
    // <br /><br />
    // The information will be relevant to, and will be used solely for the purpose of determining, verifying and/or auditing my or my
    // cohabiting partner's eligibility for, and for the general administration and enforcement of benefits under the ${aishName.fullName} Act.
    // <br /><br />
    // This consent is valid for the taxation year prior to the year of signature, the current taxation year and for each subsequent taxation
    // year for which assistance is requested.`,
    label: `I authorize the ${CRA_NAME_LONG} to release information required from my tax file to the 
    Alberta Ministry of ${MinistryList.scss.displayValue}. The information will be relevant to and used solely for the 
    purpose of determining and verifying my eligibility, or the eligibility of my co-habiting partner, for benefits under 
    the <em>${aishName.fullName} Act</em>, and the general administration and enforcement of the benefit programs. 
    This authorization is valid for the taxation year prior to the year of signature of this consent, the current taxation year, 
    and each subsequent consecutive taxation year for which assistance is requested. I understand that if I wish to withdraw this consent, 
    I may do so by writing to the Alberta Ministry of ${MinistryList.scss.displayValue}.`,
  },
  agreeDeclaration: {
    label: 'I have read and agree to the Declaration above.',
  },
  agreeDeclarationContent: {
    label: `
       I declare the information I am giving about me, my spouse/partner (if applicable) and my dependent child(ren) (if applicable) is true and complete, and I understand that intentionally withholding information or giving false or incomplete information is an offence which could result in criminal charges.
    `,
  },

  //declaration is different from digital and document (download)
  downloadDeclarationContent1: {
    label: `I declare the information I am giving about me, my spouse/partner (if applicable) and my dependent child(ren) (if applicable) is true and complete, and I understand that intentionally withholding information or giving false or incomplete information is an offence which could result in criminal charges.`,
  },
  downloadDeclarationContent2: {
    label: `If I am a Guardian, Co-decision-maker, Agent, Trustee or Attorney (under a Power of Attorney), I understand what this declaration means as it applies to the applicant.`,
  },
};

export interface CppLabelsLookup extends IntakeLabel {
  label: string;
  orderedListItem?: number;
  excludeFromPdf?: boolean;
  placeHolder?: boolean;
}
export const AishCppLabels: { [name: string]: CppLabelsLookup } = {
  cppItem1: {
    orderedListItem: 1,
    label: `I understand ${aishName.abbreviation} and ${adapName.abbreviation} require applicants to use all available income, and that CPP-D is a 
    benefit I may be entitled to.`,
  },
  cppItem2: {
    orderedListItem: 2,
    label: `If I am eligible for ${aishName.abbreviation} or ${adapName.abbreviation} benefits, I agree to have a CPP-D representative decide 
    if I am eligible for CPP-D benefits. If the CPP-D representative decides I am not eligible for CPP-D based on my earnings and contributions, 
    they will share that information with ${aishName.abbreviation} and ${adapName.abbreviation}, 
    and I will not need to apply for CPP-D. `,
  },
  cppItem3: {
    orderedListItem: 3,
    label: `To decide my eligibility for CPP-D, I give my permission to ${aishName.abbreviation} and ${adapName.abbreviation} to share 
    the following information with CPP-D:
    <ul>
      <li>${diaName.abbreviation} Medical Report filled out by a health professional, and any other reports or documents that will help the 
      programs decide my medical eligibility; and</li>
      <li>my completed ${diaName.abbreviation} Applicant Information form.</li>
    </ul>`,
  },
  cppItem4: {
    orderedListItem: 4,
    label: `To decide my eligibility for, and the amount of my ${aishName.abbreviation} or ${adapName.abbreviation} benefits, 
    I give my permission to CPP-D to share the following information with ${aishName.abbreviation} and ${adapName.abbreviation}: 
    <ul>
      <li>CPP-D will tell ${aishName.abbreviation} and ${adapName.abbreviation} whether or not I need to apply for CPP-D; and</li>
      <li>CPP-D's decision about my CPP-D benefit and the amount of the benefit I will receive.</li>
    </ul>`,
  },
  cppItem5: {
    orderedListItem: 5,
    label: `I understand I may withdraw my consent, in writing, at any time, and that this consent is in 
    place for three years from the date I (or my guardian/codecision-maker/agent/Trustee/attorney) sign it. 
    Please note: Withdrawing consent may impact your eligibility for ${aishName.abbreviation} and ${adapName.abbreviation} benefits.`,
  },
};
